export enum AlpacaFeeType {
  REG = 'REG',
  TAF = 'TAF',
  CAT = 'CAT',
  WIRE_OUT_INTERNATIONAL = 'WIRE_OUT_INTERNATIONAL',
}

/** How a saved fee row was made: MANUAL = worked out by us and taken from the user, SHADOW = saved only (test mode). */
export enum AlpacaFeeRowSource {
  MANUAL = 'MANUAL',
  SHADOW = 'SHADOW',
  ALPACA = 'ALPACA',
}

/** One Alpaca fee (madkhol-alpaca `alpaca_fee`), as the fees screen shows it. */
export type AlpacaFee = {
  id: number;
  feeType: AlpacaFeeType;
  /** What Alpaca bills us: per USD sold (REG), per share (TAF, CAT), or flat per wire. */
  alpacaFeeValue: number;
  /** What we may charge users instead. */
  platformFeeValue: number | null;
  /** True: users pay our value. False: users pay Alpaca's. */
  usePlatformFeeValue: boolean;
  /** False: paused — users pay 0 for this fee. */
  isActive: boolean;
  alpacaMaxFeePerTrade: number | null;
  platformMaxFeePerTrade: number | null;
  feeReference: string | null;
  /** When the fee was last paused. Null while active. */
  pausedSince: string | null;
};

/** Send only the fields to change. `null` clears a nullable field. */
export type UpdateAlpacaFeeRequest = {
  id: number;
  isActive?: boolean;
  usePlatformFeeValue?: boolean;
  alpacaFeeValue?: number;
  platformFeeValue?: number | null;
  alpacaMaxFeePerTrade?: number | null;
  platformMaxFeePerTrade?: number | null;
  feeReference?: string | null;
};

/** One fee type × source of a New York month, to compare with Alpaca's invoice. */
export type AlpacaFeeMonthlyReportRow = {
  id: number;
  month: string;
  feeType: AlpacaFeeType;
  source: AlpacaFeeRowSource;
  alpacaFeeShareRowCount: number;
  /** What users paid. */
  feeAmountSum: number;
  /** What Alpaca bills us. */
  alpacaPartSum: number;
  /** Ours: users paid − Alpaca bills. Negative when we covered a cost. */
  platformPartSum: number;
  updatedAt: string;
};

/** One fee charged on one order (madkhol-alpaca `alpaca_fee_share`). */
export type AlpacaFeeShare = {
  id: number;
  /** The `order` row (fund order) it belongs to. */
  orderId: number | null;
  alpacaOrderId: string | null;
  feeType: AlpacaFeeType;
  madkholOrderId: string | null;
  /** Set on the wire fee: the redemption wire it was split from. */
  alpacaTransferId: number | null;
  /** The New York day it counts on in the monthly report. */
  tradeDate: string | null;
  source: AlpacaFeeRowSource;
  /** What the fee was worked out on: sell value (REG), shares (TAF, CAT), or the order's USD (wire split). */
  base: number;
  /** What the user paid. */
  feeAmount: number;
  alpacaPart: number;
  platformPart: number;
  /** When it was taken from the user. Null = not taken (SHADOW, or not yet). */
  appliedAt: string | null;
};

export enum DividendStatus {
  PENDING = 'pending',
  REINVESTED = 'reinvested',
}

/** One Alpaca dividend buy (madkhol-alpaca `dividend_order`). */
export type DividendOrder = {
  id: number;
  alpacaOrderId: string | null;
  symbol: string;
  /** `pending` until Alpaca answers, then Alpaca's status; `failed` if Alpaca refused it twice. */
  status: string;
  /** The dividends it bought, summed. */
  amount: number;
  catFee: number;
  /** Whole shares ordered (non-fractionable fund). Null → bought by notional. */
  qty: number | null;
  retryCount: number;
  notional: number;
  filledQty: number | null;
  filledAvgPrice: number | null;
  createdAt: string;
};

/** One dividend received, or the leftover cash of a buy, waiting or reinvested. */
export type Dividend = {
  id: number;
  /** The Alpaca `DIV` event. Null for a leftover row. */
  ntaId: string | null;
  leftoverOfDividendOrderId: number | null;
  symbol: string;
  amount: number;
  status: DividendStatus;
  dividendOrderId: number | null;
  dividendDate: string;
  dividendOrder: DividendOrder | null;
  leftoverOfDividendOrder: DividendOrder | null;
};

export type FetchUserDividendsRequest = { userId: number; limit: number; offset: number };

export type PaginatedDividends = { items: Dividend[]; totalItems: number };
